import type { FC } from 'react'

import { useReducedMotion } from '@/hooks/use-reduced-motion'

const PLAYERS = [
    { id: '48213', currency: 'USDT' },
    { id: '10027', currency: 'SOL' },
    { id: '77310', currency: 'USDC' },
    { id: '52044', currency: 'USDT' },
    { id: '61905', currency: 'SOL' }
]

const WORKER_ROWS = ['Serves the games', 'Accepts every bet', 'Proves with your VRF key', 'Signs every record']

const ENDPOINTS = [
    { name: 'debit', note: 'takes the stake' },
    { name: 'credit', note: 'pays with proof' },
    { name: 'balance', note: 'read only' }
]

const rowY = (index: number): number => 96 + index * 82
const anchorY = (index: number): number => rowY(index) + 28
const portY = (index: number): number => 220 + index * 30

const toWorker = (index: number): string => `M 240 ${anchorY(index)} C 281 ${anchorY(index)} 281 ${portY(index)} 322 ${portY(index)}`
const toActor = (index: number): string => `M 562 ${portY(index)} C 594 ${portY(index)} 594 ${anchorY(index)} 626 ${anchorY(index)}`
const toWallet = (index: number): string => `M 878 ${anchorY(index)} C 919 ${anchorY(index)} 919 ${portY(index)} 960 ${portY(index)}`

interface PacketProps {
    path: string
    color: string
    duration: number
    delay: number
    reverse?: boolean
}

const Packet: FC<PacketProps> = ({ path, color, duration, delay, reverse = false }) => {
    const begin = `${-delay}s`

    return (
        <circle r='3.2' fill={color}>
            <animateMotion
                dur={`${duration}s`}
                begin={begin}
                repeatCount='indefinite'
                path={path}
                keyPoints={reverse ? '1;0' : '0;1'}
                keyTimes='0;1'
                calcMode='linear'
            />
            <animate attributeName='opacity' values='0;1;1;0' keyTimes='0;0.12;0.85;1' dur={`${duration}s`} begin={begin} repeatCount='indefinite' />
        </circle>
    )
}

/** Desktop diagram: players, the Worker and its actors inside your account, then your wallet. */
const ServerDiagram: FC = () => {
    const reduced = useReducedMotion()

    return (
        <svg viewBox='0 0 1200 540' role='img' aria-labelledby='server-diagram-title' className='h-auto w-full'>
            <title id='server-diagram-title'>
                Players connect to one game server Worker in your Cloudflare account, which runs one actor per player and currency and calls your wallet to debit, credit and read balances.
            </title>

            <text x='20' y='62' fontSize='11' letterSpacing='0.16em' className='fill-steel font-mono'>PLAYERS</text>

            {PLAYERS.map((player, index) => (
                <g key={player.id}>
                    <rect x='20' y={rowY(index)} width='220' height='56' rx='16' fill='#121216' stroke='rgba(255, 255, 255, 0.08)' />
                    <circle cx='50' cy={rowY(index) + 28} r='13' fill='#1d1d22' stroke='rgba(255, 255, 255, 0.1)' />
                    <circle cx='50' cy={rowY(index) + 24} r='3.6' fill='rgba(244, 242, 238, 0.7)' />
                    <path d={`M 43 ${rowY(index) + 35} a 7 6 0 0 1 14 0`} fill='rgba(244, 242, 238, 0.7)' />
                    <text x='74' y={rowY(index) + 24} fontSize='12' className='fill-bone font-mono'>player_{player.id}</text>
                    <text x='74' y={rowY(index) + 41} fontSize='10' className='fill-steel font-mono'>{player.currency} session</text>
                </g>
            ))}

            <rect x='290' y='28' width='620' height='484' rx='30' fill='rgba(255, 91, 31, 0.025)' stroke='rgba(255, 91, 31, 0.38)' strokeDasharray='6 7' />
            <text x='318' y='62' fontSize='11' letterSpacing='0.16em' className='fill-ember font-mono'>YOUR CLOUDFLARE ACCOUNT</text>
            <text x='626' y='62' fontSize='10' letterSpacing='0.12em' className='fill-steel font-mono'>ONE ACTOR PER PLAYER AND CURRENCY</text>

            <rect x='322' y='130' width='240' height='300' rx='22' fill='#0f0f12' stroke='rgba(255, 91, 31, 0.5)' />
            <text x='346' y='172' fontSize='20' fontWeight='600' className='fill-bone font-sans'>Game server</text>
            <text x='346' y='194' fontSize='10' className='fill-steel font-mono'>One Worker, your hostname</text>
            <line x1='346' x2='538' y1='214' y2='214' stroke='rgba(255, 255, 255, 0.08)' />

            {WORKER_ROWS.map((row, index) => (
                <g key={row}>
                    <rect x='346' y={238 + index * 36} width='7' height='7' rx='1.5' fill='#FF5B1F' />
                    <text x='362' y={245 + index * 36} fontSize='13' className='fill-bone/80 font-sans'>{row}</text>
                </g>
            ))}

            <rect x='346' y='384' width='148' height='26' rx='13' fill='rgba(75, 227, 160, 0.08)' stroke='rgba(75, 227, 160, 0.3)' />
            <text x='362' y='401' fontSize='10' className='fill-mint font-mono'>Always on latest</text>

            {PLAYERS.map((player, index) => (
                <g key={`actor-${player.id}`}>
                    <rect x='626' y={rowY(index)} width='252' height='56' rx='16' fill='#121216' stroke='rgba(255, 255, 255, 0.08)' />
                    <path
                        d={`M 652 ${rowY(index) + 20} l 10 -5 l 10 5 v 12 l -10 5 l -10 -5 Z M 652 ${rowY(index) + 20} l 10 5 l 10 -5 M 662 ${rowY(index) + 25} v 12`}
                        fill='none'
                        stroke='rgba(255, 91, 31, 0.8)'
                        strokeWidth='1.3'
                        strokeLinejoin='round'
                    />
                    <text x='686' y={rowY(index) + 24} fontSize='9' letterSpacing='0.12em' className='fill-steel font-mono'>DURABLE OBJECT</text>
                    <text x='686' y={rowY(index) + 41} fontSize='12' className='fill-bone font-mono'>{player.id} · {player.currency}</text>
                </g>
            ))}

            <rect x='960' y='130' width='220' height='300' rx='22' fill='#0f0f12' stroke='rgba(255, 255, 255, 0.16)' />
            <text x='984' y='172' fontSize='20' fontWeight='600' className='fill-bone font-sans'>Your wallet</text>
            <text x='984' y='194' fontSize='10' className='fill-steel font-mono'>The only spend authority</text>
            <line x1='984' x2='1156' y1='214' y2='214' stroke='rgba(255, 255, 255, 0.08)' />

            {ENDPOINTS.map((endpoint, index) => (
                <g key={endpoint.name}>
                    <rect x='984' y={234 + index * 48} width='172' height='36' rx='10' fill='rgba(255, 255, 255, 0.03)' stroke='rgba(255, 255, 255, 0.08)' />
                    <text x='998' y={257 + index * 48} fontSize='12' className='fill-bone font-mono'>{endpoint.name}</text>
                    <text x='1144' y={257 + index * 48} fontSize='9.5' textAnchor='end' className='fill-steel font-mono'>{endpoint.note}</text>
                </g>
            ))}

            <text x='984' y='404' fontSize='10' className='fill-steel font-mono'>Signed both ways</text>

            {PLAYERS.map((player, index) => (
                <g key={`wires-${player.id}`} fill='none' stroke='rgba(244, 242, 238, 0.14)' strokeWidth='1.2'>
                    <path d={toWorker(index)} />
                    <path d={toActor(index)} />
                    <path d={toWallet(index)} />
                </g>
            ))}

            {!reduced && PLAYERS.map((player, index) => (
                <g key={`packets-${player.id}`}>
                    <Packet path={toWorker(index)} color='#FF5B1F' duration={2.4} delay={index * 0.47} />
                    <Packet path={toActor(index)} color='#FF5B1F' duration={1.7} delay={index * 0.47 + 0.6} />
                    <Packet path={toWallet(index)} color='#FF5B1F' duration={1.9} delay={index * 0.47 + 0.2} />
                    <Packet path={toWallet(index)} color='#4BE3A0' duration={1.9} delay={index * 0.47 + 1.15} reverse />
                </g>
            ))}
        </svg>
    )
}

export default ServerDiagram
